import React from "react";
// Wallet Balance

// Default
import { useState } from "react";

// Mui
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import Divider from "@mui/material/Divider";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Grid from "@mui/material/Grid2";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import RefreshIcon from "@mui/icons-material/Refresh";

// Custom
import CustomInput from "../../forms/input/Input";
import Layout from "../../layout";
import ContainedButton from "../../forms/button/ContainedButton";
import DateAndTimePopover from "../popovers/DateAndTimePopover";
import CustomToast from "../toast/CustomToast";
import BackdropSpinner from "../spinner/BackdropSpinner";
import BillingDetailsModal from "../modals/BillingDetailsModal";
import CustomSearch from "../../components/search/Search";

// React icons
import { FaArrowUp } from "react-icons/fa";
import { FaArrowDown } from "react-icons/fa";
import { FaArrowRight } from "react-icons/fa";

// Svg
import Calendar2 from "../../assets/svg/calendar2";

// Redux
import {
  setBalance,
  setTransactionsHistory,
  setWorkOrderHistory,
  setBillingDeterminants,
  setWorkOrderId,
  setBillingData,
  setSelectedBillingData,
  setSelectedBillingDeterminant,
  setSelectedMeter,
  setWalletBalance,
} from "../../redux/features/BillingDeterminantSlice";

// Utils
import { fetchGETRequest, fetchPUTRequest } from "../../utils/services";
import {
  getTimeStamp,
  formatTime,
  formatDate,
  isUUID,
} from "../../utils/helper";
import useEmailJS from "../../hooks/useEmailJS";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { ToastState, TransactionItem } from "../../types";
import { UnifiedIcon } from "../Icon/UnifiedIcon";

interface WorkOrderItem {
  DeviceID: string;
  LastUpdatedAt: string;
  Command: string;
  CreatedAt: string;
  WorkOrderID: string;
  type?: string;
  timestamp?: string;
  workOrderId?: string | null;
}

interface BillingItem {
  id: number;
  fromDate: string;
  toDate: string;
  timestamp: string;
}

interface APIResponse {
  data?: {
    data?: WorkOrderItem[];
  };
  WorkOrderID?: string;
  Status?: string;
  IP_KWH?: Array<{ value: string }>;
  EventCode?: Array<{ value: string }>;
  [key: string]: any;
}

interface EmailData {
  balance: number;
  message: string;
  email: string;
}

const WalletBalance: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [timeRange, setTimeRange] = useState<number[]>([0, 1439]);
  const [rechargeAmount, setRechargeAmount] = useState("");
  const [toast, setToast] = useState<ToastState>({
    message: "",
    severity: "info",
  });
  const [loader, setLoader] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [connectLoading, setConnectLoading] = useState(false);
  const [disconnectLoading, setDisconnectLoading] = useState(false);
  const dispatch = useAppDispatch();

  const {
    balance,
    transactionsHistory,
    workOrderHistory,
    billingDeterminants,
    billingData,
    selectedBillingDeterminant,
    selectedBillingData,
    selectedMeter,
    walletBalance,
    workOrderId,
  } = useAppSelector((state) => state.billing);

  const allMeters = [selectedMeter];

  const { sendEmail } = useEmailJS();

  const AppSendMail = async (balance: number, message: string) => {
    const emailData: EmailData = {
      balance: balance,
      message: message,
      email: "kaushik@voltreum.com",
    };

    console.log(emailData, "---");

    await sendEmail(emailData, selectedMeter)
      .then((res: any) => {
        console.log("Email sent:", res?.text);
        setToast({
          message: "Email sent successfully!",
          severity: "success",
        });
      })
      .catch((err: unknown) => {
        console.error("Failed to send email:", err);
        setToast({
          message: err instanceof Error ? err.message : "Email failed",
          severity: "error",
        });
      });
  };

  const fetchWorkOrders = async () => {
    if (!selectedMeter) {
      setToast({
        message: "Please Enter the meter serial No.",
        severity: "error",
      });
      return;
    }

    if (!isUUID(selectedMeter)) {
      setToast({
        message: "Please Enter the valid meter serial No.",
        severity: "error",
      });
      setAnchorEl(null);
      return;
    }

    try {
      const response: any = await fetchGETRequest(
        `devices/${selectedMeter}/workorders`,
        {},
        true,
      );

      const filterData = response?.data?.data?.filter(
        (item: WorkOrderItem) => item?.DeviceID === selectedMeter,
      );
      const sortedData = filterData?.sort(
        (a: WorkOrderItem, b: WorkOrderItem) =>
          new Date(b.LastUpdatedAt).getTime() -
          new Date(a.LastUpdatedAt).getTime(),
      );
      dispatch(setWorkOrderHistory(sortedData || []));
    } catch (error) {
      console.log(error);
    }
  };

  // useEffect(() => {
  //   if (selectedMeter) {
  //     fetchWorkOrders();
  //   }
  // }, [selectedMeter]);

  const handleDateChange = (dates: [Date, Date]) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  };

  const handleTimeRangeChange = (timeRange: number | number[]) => {
    if (Array.isArray(timeRange)) {
      setTimeRange(timeRange);
    } else {
      setTimeRange([timeRange, timeRange]);
    }
  };

  const TransactionHistoryCard = (
    id: number | string,
    animate: boolean,
    amount: number,
    timestamp: string,
    index: number,
  ) => {
    const isPositive = amount > 0;
    return (
      <Box
        sx={{
          background: isPositive ? "#e1f5d7" : "#f7cdcb",
          borderLeftColor: isPositive ? "#9CE976" : "#FF3B30",
        }}
        className="wallet_balance_transaction_history_row"
        key={index}
      >
        <Stack direction={"row"} alignItems={"center"} gap={"5px"}>
          <UnifiedIcon
            icon={isPositive ? FaArrowUp : FaArrowDown}
            color={isPositive ? "#9CE976" : "#FF3B30"}
            fontSize="15px"
          />
          <Typography variant="body2">{Number(amount).toFixed(2)}</Typography>
        </Stack>
        <Typography variant="body2">
          {formatDate(timestamp, "DD-MM-YYYY HH:mm:ss")}
        </Typography>
      </Box>
    );
  };

  const WorkOrderHistoryCard = (item: WorkOrderItem, index: number) => {
    return (
      <Box
        className="wallet_balance_work_order_history_row"
        sx={{ background: "#e3f1fa" }}
        key={index}
      >
        <Stack
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <Typography
            variant="body2"
            fontWeight={"600"}
            color={item?.Command === "DISCONNECT" ? "#FF3B30" : "#9CE976"}
          >
            {item?.Command === "DISCONNECT"
              ? "Disconnect Work Order"
              : "Reconnect Work Order"}
          </Typography>
          <Typography variant="body2" fontWeight={400}>
            {formatDate(item?.CreatedAt, "DD-MM-YYYY HH:mm:ss")}
          </Typography>
        </Stack>

        <Typography variant="body2" mt={"10px"} fontWeight={500}>
          Work Order ID:{" "}
          <Typography variant="caption"> {item?.WorkOrderID}</Typography>
        </Typography>
      </Box>
    );
  };

  const GenerateBillingDeterminantCard = (item: BillingItem, index: number) => {
    return (
      <Box
        className="wallet_balance_billing_determinant_row"
        sx={{
          backgroundColor: "#e3f1fa",
          "&:hover": {
            backgroundColor: "#e0f7fa",
          },
          cursor: "pointer",
        }}
        onClick={() => {
          const savedBillingData = billingData?.filter(
            (bill: any) => bill?.id === item?.id,
          );
          dispatch(setSelectedBillingData(savedBillingData));
          dispatch(setSelectedBillingDeterminant(item));
          setOpenModal(true);
        }}
        key={index}
      >
        <Typography variant="body2" fontWeight={"500"}>
          Billing Determinant:{" "}
          {formatDate(new Date(item?.fromDate), "DD/MM/YYYY HH:mm:ss")}{" "}
          <Typography component="span">
            <UnifiedIcon icon={FaArrowRight} color="#181B25" fontSize="10px" />
          </Typography>{" "}
          {formatDate(new Date(item?.toDate), "DD/MM/YYYY HH:mm:ss")}{" "}
        </Typography>
        <Typography variant="body2" fontWeight={400}>
          {formatDate(item?.timestamp, "DD-MM-YYYY HH:mm:ss")}
        </Typography>
      </Box>
    );
  };

  const createWorkOrder = async (type: string) => {
    if (!selectedMeter) {
      setToast({
        message: "Please Enter the meter serial No.",
        severity: "error",
      });
      return;
    }
    if (!isUUID(selectedMeter)) {
      setToast({
        message: "Please Enter the valid meter serial No.",
        severity: "error",
      });
      setAnchorEl(null);
      return;
    }

    try {
      const newWorkOrder = {
        id: workOrderHistory?.length + 1,
        type: type,
        timestamp: new Date().toISOString(),
        workOrderId: null as string | null,
      };

      let body = {
        deviceIds: [selectedMeter],
        command: "",
        projectName: "",
      };

      if (type === "disconnect") {
        body.command = "ExecuteDisconnect";
      } else {
        body.command = "ExecuteConnect";
      }

      const response: any = await fetchPUTRequest(`devices/command`, body);
      if (response) {
        newWorkOrder.workOrderId = response?.WorkOrderID || null;
        dispatch(setWorkOrderHistory([...workOrderHistory, newWorkOrder]));
      }
    } catch (error) {
      setToast({
        message: error instanceof Error ? error.message : "An error occurred",
        severity: "error",
      });
    }
  };

  const handleFetchLastRecharge = async () => {
    if (!selectedMeter) {
      setToast({
        message: "Please Enter the meter serial No.",
        severity: "error",
      });
      return;
    }

    if (!isUUID(selectedMeter)) {
      setToast({
        message: "Please Enter the valid meter serial No.",
        severity: "error",
      });
      setAnchorEl(null);
      return;
    }

    try {
      setLoader(true);
      const body = {
        command: "GetLastTokenRechargeAmount",
        deviceIds: allMeters,
      };
      const response: any = await fetchPUTRequest(`devices/command`, body);

      if (response && response.WorkOrderID) {
        dispatch(setWorkOrderId(response.WorkOrderID));
        // const workOrderHistoryDetails = await fetchGETRequest(
        //   `workorders/${response?.WorkOrderID}`
        // );
      }
    } catch (error) {
      setToast({
        message:
          "Wallet balance unavailable. Possible network delay or data source issue. Please try again later.",
        severity: "error",
      });
    } finally {
      setLoader(false);
    }
  };

  const submitRechargeAmount = async () => {
    if (!selectedMeter) {
      setToast({
        message: "Please Enter the meter serial No.",
        severity: "error",
      });
      return;
    }

    if (!isUUID(selectedMeter)) {
      setToast({
        message: "Please Enter the valid meter serial No.",
        severity: "error",
      });
      setAnchorEl(null);
      return;
    }

    setToast({
      message: "Recharge Updation in process, Try refresh after some time.",
      severity: "info",
    });
    const body = {
      command: "SetLastTokenRechargeAmount",
      value: rechargeAmount,
      deviceIds: allMeters,
    };
    const response: any = await fetchPUTRequest(
      // `devices/${selectedMeter}/prepaidparam`,
      `devices/prepaidparam`,
      body,
    );

    if (response && response.WorkOrderID) {
      dispatch(setWorkOrderId(response.WorkOrderID));

      const workOrderHistoryDetails = await fetchGETRequest(
        `workorders/${response.WorkOrderID}`,
      );
    }
  };

  const handleFetchWalletBalance = async () => {
    try {
      if (!selectedMeter) {
        setToast({
          message: "Please Enter the meter serial No.",
          severity: "error",
        });
        return;
      }

      if (!isUUID(selectedMeter)) {
        setToast({
          message: "Please Enter the valid meter serial No.",
          severity: "error",
        });
        setAnchorEl(null);
        return;
      }

      const body = {
        command: "GetCurrentBalanceAmount",
        deviceIds: [selectedMeter],
        projectName: "",
      };
      setLoader(true);
      const response: any = await fetchPUTRequest(`devices/command`, body);

      if (response && response.WorkOrderID) {
        setToast({
          message: `The request with ID = ${response.WorkOrderID} has been placed successfully to the prepaid engine. The engine may take several seconds to respond. Click the refresh button in some time.`,
          severity: "info",
        });
        dispatch(setWorkOrderId(response.WorkOrderID));

        // setTimeout(async () => {

        //   const workOrderHistoryDetails = await fetchGETRequest(
        //     `workorders/${response?.WorkOrderID}`
        //   );
        //   if (workOrderHistoryDetails?.Status !== "IN PROGRESS") {
        //     dispatch(setBalance(workOrderHistoryDetails?.Status));
        //   }
        // }, 20000);
      }
    } catch (error) {
      setToast({
        message:
          "Wallet balance unavailable. Possible network delay or data source issue. Please try again later.",
        severity: "error",
      });
    } finally {
      setLoader(false);
    }
  };

  const submitUpdateBalance = async () => {
    if (!selectedMeter) {
      setToast({
        message: "Please Enter the meter serial No.",
        severity: "error",
      });
      return;
    }

    if (!isUUID(selectedMeter)) {
      setToast({
        message: "Please Enter the valid meter serial No.",
        severity: "error",
      });
      setAnchorEl(null);
      return;
    }

    setToast({
      message: "Balance updation in progress, Try refresh after some time.",
      severity: "info",
    });
    try {
      const newBalance = Number(balance) + Number(walletBalance);
      const body = {
        command: "SetCurrentBalanceAmount",
        value: String(newBalance),
        deviceIds: [selectedMeter],
        projectName: "",
      };

      // setLoader(true);
      const response: any = await fetchPUTRequest(`devices/command`, body);

      if (response && response.WorkOrderID) {
        if (newBalance > 20) {
          AppSendMail(newBalance, `Your New Meter Wallet balance is: `);
        } else if (newBalance < 20) {
          AppSendMail(
            newBalance,
            `Your Meter Wallet balance is not sufficient: `,
          );
        }

        setTimeout(() => {
          fetchWorkOrders();
          handleUpdateBalance();
        }, 30000);
        dispatch(setWorkOrderId(response.WorkOrderID));
      }
    } catch (error) {
      setToast({
        message: error instanceof Error ? error.message : "An error occurred",
        severity: "error",
      });
    } finally {
      // setLoader(false);
    }
  };

  const handleUpdateBalance = async () => {
    if (walletBalance) {
      const newBalance = Number(balance) + Number(walletBalance);
      const newTransaction: TransactionItem = {
        id: transactionsHistory?.length + 1,
        amount: Number(walletBalance),
        timestamp: new Date().toISOString(),
        animate: true,
      };

      dispatch(
        setTransactionsHistory([...transactionsHistory, newTransaction]),
      );
      if (balance >= 20 && newBalance < 20) {
        createWorkOrder("disconnect");
        setToast({
          message:
            "Wallet balance is below 20. Disconnect is in progress. check after sometime.",
          severity: "error",
        });
      } else if (balance < 20 && newBalance >= 20) {
        createWorkOrder("reconnect");
        setToast({
          message:
            "Wallet balance is above 20. Reconnect is in progress. check after sometime.",
          severity: "success",
        });
      }
      dispatch(setWalletBalance(""));
    }
  };

  const handleBillingDeterminant = async () => {
    if (!selectedMeter) {
      setToast({
        message: "Please Enter the meter serial No.",
        severity: "error",
      });
      setAnchorEl(null);
      return;
    }

    if (!isUUID(selectedMeter)) {
      setToast({
        message: "Please Enter the valid meter serial No.",
        severity: "error",
      });
      setAnchorEl(null);
      return;
    }
    // const deviceId = "30d33fa0-bb60-11ef-85d5-e5b1b2e5df91"
    const startTs = getTimeStamp(formatTime(timeRange[0]), new Date(startDate));
    const endTs = getTimeStamp(formatTime(timeRange[1]), new Date(endDate));
    const keys = "IP_KWH,EventCode";
    const intervalType = "WEEK";

    const response: any = await fetchGETRequest(
      `plugins/telemetry/DEVICE/${selectedMeter}/getBillingDeterminants?keys=${keys}&startTs=${startTs}&endTs=${endTs}&intervalType=${intervalType}`,
    );

    if (response?.IP_KWH && response?.IP_KWH?.length > 0) {
      const values = response?.IP_KWH?.map((item: { value: string }) =>
        parseFloat(item.value),
      );
      const max = Math.max(...values);
      const min = Math.min(...values);
      const totalCost = max - min;

      let penaltyCount = 0;

      const penalty = response?.EventCode?.map((item: { value: string }) => {
        if (parseFloat(item.value) >= 200 && parseFloat(item.value) < 300) {
          return penaltyCount++;
        }
      });

      const newBalance = balance - totalCost;

      const newBillingDeterminant: BillingItem = {
        id: billingDeterminants?.length + 1,
        fromDate: formatDate(startTs, "YYYY-MM-DDTHH:mm"),
        toDate: formatDate(endTs, "YYYY-MM-DDTHH:mm"),
        timestamp: new Date().toISOString(),
      };

      const newTransaction: TransactionItem = {
        id: transactionsHistory?.length + 1,
        amount: -totalCost,
        timestamp: new Date().toISOString(),
        animate: true,
      };

      dispatch(
        setTransactionsHistory([...transactionsHistory, newTransaction]),
      );

      dispatch(
        setBillingDeterminants([...billingDeterminants, newBillingDeterminant]),
      );

      const newBillingData = {
        id: newBillingDeterminant.id,
        seqNo: billingDeterminants.length + 1,
        registerType: selectedMeter,
        qu: max.toFixed(2),
        activeEnergy: "1.0.1.8.0.255",
        ut: min.toFixed(2),
        qt: totalCost.toFixed(2),
        penaltyCount,
      };

      dispatch(setBillingData([...billingData, newBillingData]));
      // localStorage.setItem(`billingData_${newBillingDeterminant.id}`, JSON.stringify(newBillingData));
      // dispatch(setBalance(newBalance));
      setStartDate(new Date());
      setEndDate(new Date());
      setTimeRange([0, 1439]);

      // if (newBalance < 20) {
      //   // await createWorkOrder('disconnect');
      //   setToast({ message: "Wallet balance is below 20. Disconnect work order created.", severity: "error" });
      // }
    } else {
      setStartDate(new Date());
      setEndDate(new Date());
      setTimeRange([0, 1439]);
      setToast({
        message: "No billing data found for the selected time range.",
        severity: "error",
      });
    }

    setAnchorEl(null);
  };

  const bulkConnectAndDisConnect = async (type: string) => {
    try {
      if (type === "disconnect") {
        setDisconnectLoading(true);
      } else {
        setConnectLoading(true);
      }

      setToast({
        message: "Bulk action in progress.",
        severity: "info",
      });

      const response: any = await fetchGETRequest(
        `tenant/devices/?pageSize=20&page=0`,
      );

      if (response?.data?.length > 0) {
        let body = {
          deviceIds: response?.data?.map((item: any) => item?.id?.id),
          command: "",
          projectName: "",
        };
        if (type === "disconnect") {
          body.command = "ExecuteDisconnect";
        } else {
          body.command = "ExecuteConnect";
        }
        await fetchPUTRequest(`devices/command`, body);
      }
    } catch (error) {
      setToast({
        message: error instanceof Error ? error.message : "An error occurred",
        severity: "error",
      });
    } finally {
      setDisconnectLoading(false);
      setConnectLoading(false);
    }
  };

  return (
    <Layout>
      <BackdropSpinner open={loader} />
      <CustomToast
        open={toast?.message}
        severity={toast?.severity}
        setToast={setToast}
      />

      <Grid container spacing={2.25}>
        <Grid size={{ xs: 12, md: 7, lg: 7 }}>
          <Card
            sx={{
              display: "flex",
              flexDirection: "column",
              overflow: "auto",
            }}
          >
            <CardHeader
              title={
                <Stack
                  direction={"row"}
                  justifyContent={"space-between"}
                  alignItems={"center"}
                >
                  <Typography variant="body1" fontWeight={500}>
                    Billing Details
                  </Typography>

                  <Stack
                    direction={"row"}
                    alignItems={"center"}
                    justifyContent={"space-between"}
                    gap={1}
                  >
                    <Typography
                      variant="caption"
                      fontWeight={"600"}
                      textAlign={"left"}
                    >
                      Meter Serial No:
                    </Typography>
                    <Box sx={{ width: "400px" }}>
                      <CustomSearch
                        sx={{ width: "100%" }}
                        value={selectedMeter}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          dispatch(setSelectedMeter(e.target.value));
                        }}
                        onClick={fetchWorkOrders}
                      />
                    </Box>
                  </Stack>
                </Stack>
              }
            />
            <Divider />

            <CardContent
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "15px",
                flexGrow: 1,
              }}
            >
              <Stack
                direction={"row"}
                justifyContent={"space-between"}
                alignItems={"center"}
              >
                <Typography variant="body1" fontWeight={"500"}>
                  Wallet Balance :{" "}
                  <Typography
                    component="span"
                    color={balance >= 0 ? "#9CE976" : "#FF3B30"}
                  >
                    &#8377; {Number(balance).toFixed(2)}
                  </Typography>{" "}
                </Typography>
                <Stack justifyContent={"center"} direction={"row"} gap={1}>
                  <ContainedButton
                    label={"Fetch Wallet Balance"}
                    sx={{
                      width: "fit-content",
                      color: "#ffffff",
                    }}
                    disabled={!selectedMeter}
                    onClick={handleFetchWalletBalance}
                  />
                  <IconButton
                    disabled={!selectedMeter || !workOrderId}
                    onClick={async () => {
                      if (workOrderId) {
                        const workOrderHistoryDetails: any =
                          await fetchGETRequest(`workorders/${workOrderId}`);
                        if (workOrderHistoryDetails?.Status !== "IN PROGRESS") {
                          const balanceValue = parseFloat(
                            workOrderHistoryDetails?.Status || "0",
                          );
                          if (!isNaN(balanceValue)) {
                            dispatch(setBalance(balanceValue));
                          }
                        } else {
                          setToast({
                            message:
                              "Prepaid engine has not responded yet. The request is still in progress. Please try after some time.",
                            severity: "info",
                          });
                        }
                      } else {
                        setToast({
                          message: "Workorder id is required.",
                          severity: "error",
                        });
                      }
                    }}
                    //loading={loading}
                  >
                    <RefreshIcon />
                  </IconButton>
                </Stack>
              </Stack>

              <Typography variant="body1">Last Updated: </Typography>
              <CustomInput
                placeholder={"Enter amount"}
                type="number"
                name={"walletBalance"}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  dispatch(setWalletBalance(e.target.value));
                }}
                value={walletBalance}
              />

              <ContainedButton
                label={"Update Balance"}
                sx={{
                  width: "100%",
                  color: "#ffffff",
                  marginTop: "auto",
                }}
                //onClick={handleUpdateBalance}
                onClick={submitUpdateBalance}
                disabled={!walletBalance}
              />
            </CardContent>
          </Card>

          <Card
            sx={{
              marginTop: "20px",
              display: "flex",
              flexDirection: "column",
              overflow: "auto",
              height: "270px",
            }}
          >
            <CardHeader
              title={
                <Stack
                  direction={"row"}
                  justifyContent={"space-between"}
                  alignItems={"center"}
                >
                  <Typography variant="body1" fontWeight={"500"}>
                    Last Recharge
                  </Typography>
                  <ContainedButton
                    label={"Fetch Last Recharge"}
                    sx={{
                      width: "fit-content",
                      color: "#ffffff",
                    }}
                    disabled={!selectedMeter}
                    onClick={handleFetchLastRecharge}
                  />
                </Stack>
              }
            />
            <Divider />
            <CardContent
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "15px",
                flexGrow: 1,
              }}
            >
              <Typography variant="body1" fontWeight={"500"}>
                Last Recharge :{" "}
                <Typography component="span" color="#9CE976">
                  &#8377; 0.00
                </Typography>{" "}
              </Typography>

              <Stack
                direction={"row"}
                justifyContent={"space-between"}
                alignItems={"center"}
              >
                <Typography variant="body1" fontWeight={"500"}>
                  Set Recharge Date:
                </Typography>
                <IconButton
                  onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
                    setAnchorEl(event.currentTarget)
                  }
                >
                  <Calendar2 />
                </IconButton>
              </Stack>

              <CustomInput
                placeholder={"Set Recharge amount"}
                type="number"
                value={rechargeAmount}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setRechargeAmount(e.target.value)
                }
              />

              <ContainedButton
                label={"Set Recharge"}
                sx={{
                  width: "100%",
                  color: "#ffffff",
                  marginTop: "auto",
                }}
                // onClick={submitRechargeAmount}
                disabled={!rechargeAmount}
              />
            </CardContent>
          </Card>

          <Card sx={{ marginTop: "20px", overflow: "auto", height: "270px" }}>
            <CardHeader
              title={
                <Stack
                  direction={"row"}
                  justifyContent={"space-between"}
                  alignItems={"center"}
                >
                  <Typography variant="body1" fontWeight={"500"}>
                    Generate Billing Determinant
                  </Typography>
                  <IconButton
                    onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
                      setAnchorEl(event.currentTarget)
                    }
                  >
                    <Calendar2 />
                  </IconButton>
                </Stack>
              }
            />
            <Divider />

            <CardContent
              sx={{ display: "flex", flexDirection: "column", gap: "15px" }}
            >
              {billingDeterminants?.map((item: BillingItem, index: number) => {
                return GenerateBillingDeterminantCard(item, index);
              })}

              {billingDeterminants?.length === 0 && (
                <Stack direction={"row"} justifyContent={"center"}>
                  <Typography variant="body1">No Data found</Typography>
                </Stack>
              )}

              <DateAndTimePopover
                anchorEl={anchorEl}
                setAnchorEl={(anchorEl: HTMLElement | null) =>
                  setAnchorEl(anchorEl)
                }
                startDate={startDate}
                endDate={endDate}
                timeRange={timeRange}
                setTimeRange={handleTimeRangeChange}
                handleDateChange={handleDateChange}
                handleDateSubmit={handleBillingDeterminant}
                btnText={"Generate Billing Determinant"}
              />
              <BillingDetailsModal
                openModal={openModal}
                setOpenModal={setOpenModal}
                selectedBillingData={selectedBillingData}
                selectedBillingDeterminant={selectedBillingDeterminant}
                setSelectedBillingDeterminant={() => {
                  dispatch(setSelectedBillingDeterminant);
                }}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 5, lg: 5 }}>
          <Card sx={{ overflow: "auto" }}>
            <CardHeader
              title={
                <Stack
                  direction={"row"}
                  justifyContent={"space-between"}
                  alignItems={"center"}
                >
                  <Typography variant="body1" fontWeight={"500"}>
                    Bulk Actions
                  </Typography>

                  <Stack
                    direction={"row"}
                    gap={"10px"}
                    alignItems={"center"}
                    width={"80%"}
                    justifyContent={"flex-end"}
                  >
                    <ContainedButton
                      label={"Bulk Connect"}
                      sx={{
                        width: "fit-content",
                        color: "#ffffff",
                        marginTop: "auto",
                      }}
                      onClick={() => bulkConnectAndDisConnect("connect")}
                      loading={connectLoading}
                    />

                    <ContainedButton
                      label={"Bulk Disconnect"}
                      bgColor={
                        "linear-gradient(180deg, #FF7F7F 0%, #FF4D4D 100%)"
                      }
                      sx={{
                        width: "fit-content",
                        color: "#ffffff",
                        marginTop: "auto",
                      }}
                      onClick={() => bulkConnectAndDisConnect("disconnect")}
                      loading={disconnectLoading}
                    />
                  </Stack>
                </Stack>
              }
            />
          </Card>
          <Card
            className="wallet_balance_transaction_history"
            sx={{ marginTop: "20px", height: "434px", overflow: "auto" }}
          >
            <CardHeader
              title={
                <Typography variant="body1" fontWeight={"500"}>
                  Transaction History
                </Typography>
              }
            />
            <Divider />
            <CardContent
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "15px",
                overflow: "auto",
              }}
            >
              {transactionsHistory?.map(
                (item: TransactionItem, index: number) => {
                  return TransactionHistoryCard(
                    item.id,
                    item.animate,
                    item.amount,
                    item.timestamp,
                    index,
                  );
                },
              )}

              {transactionsHistory?.length === 0 && (
                <Stack direction={"row"} justifyContent={"center"}>
                  <Typography variant="body1">No Data found</Typography>
                </Stack>
              )}
            </CardContent>
          </Card>

          <Card sx={{ marginTop: "20px", height: "270px", overflow: "auto" }}>
            <CardHeader
              title={
                <Typography variant="body1" fontWeight={"500"}>
                  Work Order History
                </Typography>
              }
            />
            <Divider />

            <CardContent
              sx={{ display: "flex", flexDirection: "column", gap: "10px" }}
            >
              {workOrderHistory?.map((item: WorkOrderItem, index: number) => {
                {
                  /* return WorkOrderHistoryCard(
                  item.type,
                  item.timestamp,
                  item.workOrderId
                ); */
                }
                return WorkOrderHistoryCard(item, index);
              })}

              {workOrderHistory?.length === 0 && (
                <Stack direction={"row"} justifyContent={"center"}>
                  <Typography variant="body1">No Data found</Typography>
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Layout>
  );
};

export default WalletBalance;
